import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector } from './EffectorBase';
import { PropertyTypes, ValueTypes, FadeType } from '../../../globals';
import DropdownProperty from '../../../components/properties/DropdownProperty';
import MathUtils from '../../utilities/MathUtils';
import { TimelineFragment } from '../TimelineFragment';
import { IDropdownOption } from '../../../interfaces/Misc';
import SceneObjectUtils from '../../utilities/SceneObjectUtils';

export interface EffectorObjectFadeSaveData
{
	valueAtStart: number;
	valueAtEnd: number;
	loopCount: number;
}

export default class EffectorObjectFade extends EffectorBase<EffectorObjectFade> implements LoopableEffector
{
	valueAtStart: number;
	valueAtEnd: number;
	fadeType: FadeType;
	loopCount: number;
	reversed?: boolean = undefined; //this is here just so this animation type works nicely alongside other reversible animations...
	//reversing this animation essentially just flips the fade-type

	constructor(fragment: TimelineFragment)
	{
		super(fragment);

		this.valueAtStart = 0;
		this.valueAtEnd = 1;
		this.fadeType = FadeType.fadeIn;
		this.loopCount = 1;
	}

	save = () : EffectorObjectFadeSaveData =>
	{
		const output = {
			valueAtStart: this.valueAtStart,
			valueAtEnd: this.valueAtEnd,
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorObjectFadeSaveData) =>
	{
		this.valueAtStart = savedData.valueAtStart;
		this.valueAtEnd = savedData.valueAtEnd;
		this.fadeType = (this.valueAtStart === 0) ? FadeType.fadeIn : FadeType.fadeOut;
		if (savedData.loopCount !== undefined) this.loopCount = savedData.loopCount;
	};

	setFadeType(fadeType : FadeType)
	{
		this.fadeType = fadeType;
		if (this.fadeType === FadeType.fadeIn)
		{
			this.valueAtStart = 0;
			this.valueAtEnd = 1;
		}
		else
		{
			this.valueAtStart = 1;
			this.valueAtEnd = 0;
		}
	}

	copyValues = (sourceEffector: EffectorObjectFade) =>
	{
		this.valueAtStart = sourceEffector.valueAtStart;
		this.valueAtEnd = sourceEffector.valueAtEnd;
		this.fadeType = sourceEffector.fadeType;
		this.loopCount = sourceEffector.loopCount;
	}

	reverse = () =>
	{
		const v = this.valueAtStart;
		this.valueAtStart = this.valueAtEnd;
		this.valueAtEnd = v;
		if (this.fadeType === FadeType.fadeIn) this.fadeType = FadeType.fadeOut;
		else this.fadeType = FadeType.fadeIn;
	}

	resetState = () =>
	{
		this.forEachTargetObject(function(targetObject)
		{
			targetObject.traverse((child) =>
			{
				if (child.userData.fadeInFactor !== undefined) delete child.userData.fadeInFactor;
				SceneObjectUtils.applyFadeInEffect(child);
			});
		});
	}

	update = (fragmentTime: number) =>
	{
		const interpolated = MathUtils.lerpValue(this.valueAtStart, this.valueAtEnd, fragmentTime, ValueTypes.Scalar);
		if (fragmentTime === 0)
		{
			this.forEachTargetObject(function(targetObject)
			{
				targetObject.traverse((child) =>
				{
					if (child.userData.fadeInFactor === undefined)
					{
						child.userData.fadeInFactor = interpolated;
						SceneObjectUtils.applyFadeInEffect(child);
					}
				});
			});
		}
		else
		{
			this.forEachTargetObject(function(targetObject)
			{
				targetObject.traverse((child) =>
				{
					child.userData.fadeInFactor = interpolated;
					SceneObjectUtils.applyFadeInEffect(child);
				});
			});
		}
	}

	onAdded = () =>
	{
	}

	getEditorProperties = () =>
	{
		const options: IDropdownOption[] = [];
		for (const [key, value] of Object.entries(FadeType))
		{
			options.push({ display: value, value: value });
		}
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Fade Type",
				PropertyTypes.DropDown,
				<DropdownProperty
					data-name={"property-fade-type"}
					value={this.fadeType}
					options={options}
				/>,
				() => {return this.fadeType;},
				(v) => {this.setFadeType(v);},
			),

		];
	}
}
